import { useCallback, useEffect, useState } from 'react'
import { AlertCircle, Loader2, RefreshCw } from 'lucide-react'

import { Alert, AlertDescription } from '@/components/ui/alert'
import { Badge } from '@/components/ui/badge'
import { Button } from '@/components/ui/button'
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog'
import { Label } from '@/components/ui/label'
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select'
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table'
import { log } from '@/lib/logger'
import {
  postCallTemplateService,
  type PostCallDecision,
} from '@/services/postCallTemplateService'

const STATUS_OPTIONS = [
  { value: 'all', label: 'All statuses' },
  { value: 'sent', label: 'Sent' },
  { value: 'skipped', label: 'Skipped' },
  { value: 'skipped_idempotent', label: 'Skipped (duplicate)' },
  { value: 'failed', label: 'Failed' },
  { value: 'needs_review', label: 'Needs review' },
]

function statusVariant(status: string): 'default' | 'secondary' | 'destructive' | 'outline' {
  if (status === 'sent') return 'default'
  if (status === 'failed') return 'destructive'
  if (status === 'needs_review') return 'outline'
  return 'secondary'
}

function formatDate(value: string): string {
  const date = new Date(value)
  if (Number.isNaN(date.getTime())) return value
  return date.toLocaleString()
}

function isNotFoundError(err: unknown): boolean {
  return err instanceof Error && /not found|404/i.test(err.message)
}

function resolveDecisionReason(decision: PostCallDecision): string {
  const direct = String(decision.reason || '').trim()
  if (direct) return direct
  const fromCampaign = decision.campaign_result?.reason
  if (typeof fromCampaign === 'string' && fromCampaign.trim()) return fromCampaign.trim()
  if (decision.status === 'sent') return 'sent'
  if (decision.status === 'failed') return 'campaign_send_failed'
  if (decision.status === 'skipped_idempotent') return 'skipped_idempotent'
  if (decision.status === 'skipped') return 'skipped'
  if (decision.status === 'needs_review') return 'needs_review'
  return 'No disponible'
}

type PostCallTemplateDecisionsPanelProps = {
  apiUnavailable?: boolean
  isOpen?: boolean
}

export function PostCallTemplateDecisionsPanel({ apiUnavailable = false, isOpen = true }: PostCallTemplateDecisionsPanelProps) {
  const [decisions, setDecisions] = useState<PostCallDecision[]>([])
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const [statusFilter, setStatusFilter] = useState('all')
  const [selectedDecision, setSelectedDecision] = useState<PostCallDecision | null>(null)

  const loadDecisions = useCallback(async () => {
    if (!isOpen || apiUnavailable) {
      setDecisions([])
      setError(null)
      setLoading(false)
      return
    }

    setLoading(true)
    setError(null)
    try {
      const response = await postCallTemplateService.listDecisions({
        status: statusFilter === 'all' ? undefined : statusFilter,
        limit: 50,
      })
      setDecisions(response.results)
    } catch (err) {
      if (isNotFoundError(err)) {
        setDecisions([])
        setError('Decision log API is not available in this local backend yet.')
        return
      }
      const message = err instanceof Error ? err.message : 'Failed to load decision logs'
      log.error('Post-call decisions load failed', { message })
      setError(message)
    } finally {
      setLoading(false)
    }
  }, [apiUnavailable, isOpen, statusFilter])

  useEffect(() => {
    if (!isOpen) return
    void loadDecisions()
  }, [isOpen, loadDecisions])

  if (!isOpen) {
    return null
  }

  return (
    <div className="space-y-4 rounded-lg border border-border/70 bg-muted/10 p-4">
      <div className="flex flex-wrap items-end justify-between gap-2">
        <div className="space-y-1">
          <h3 className="text-sm font-semibold">Decision log</h3>
          <p className="text-sm text-muted-foreground">
            Recent post-call template decisions, including campaign execution links when available.
          </p>
        </div>
        <div className="flex items-end gap-2">
          <div className="space-y-1">
            <Label htmlFor="decision-status-filter">Status</Label>
            <Select value={statusFilter} onValueChange={setStatusFilter}>
              <SelectTrigger id="decision-status-filter" className="w-[180px]">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                {STATUS_OPTIONS.map((option) => (
                  <SelectItem key={option.value} value={option.value}>
                    {option.label}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
          <Button type="button" variant="outline" size="icon" onClick={() => void loadDecisions()} disabled={loading}>
            <RefreshCw className={`h-4 w-4 ${loading ? 'animate-spin' : ''}`} />
          </Button>
        </div>
      </div>

      {error ? (
        <Alert variant={apiUnavailable ? 'default' : 'destructive'}>
          <AlertCircle className="h-4 w-4" />
          <AlertDescription>{error}</AlertDescription>
        </Alert>
      ) : null}

      {loading ? (
        <div className="flex items-center gap-2 py-6 text-sm text-muted-foreground">
          <Loader2 className="h-4 w-4 animate-spin" />
          Loading decisions…
        </div>
      ) : apiUnavailable ? (
        <div className="rounded-lg border border-dashed border-border bg-background/70 px-4 py-8 text-center text-sm text-muted-foreground">
          Decision log will populate after the post-call API is available and webhooks start running.
        </div>
      ) : decisions.length === 0 ? (
        <div className="rounded-lg border border-dashed border-border px-4 py-8 text-center text-sm text-muted-foreground">
          No post-call decisions recorded yet.
        </div>
      ) : (
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead>When</TableHead>
              <TableHead>Call</TableHead>
              <TableHead>Template</TableHead>
              <TableHead>Status</TableHead>
              <TableHead>Reason</TableHead>
              <TableHead>Campaign</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {decisions.map((decision) => (
              <TableRow
                key={decision.id}
                className="cursor-pointer hover:bg-muted/40"
                onClick={() => setSelectedDecision(decision)}
              >
                <TableCell className="whitespace-nowrap text-xs">{formatDate(decision.created_at)}</TableCell>
                <TableCell className="text-xs">
                  <div>{decision.caller_phone || '—'}</div>
                  <div className="text-muted-foreground">{decision.elevenlabs_conversation_id || decision.call_id.slice(0, 8)}</div>
                </TableCell>
                <TableCell className="text-xs">
                  {decision.selected_template_name || '—'}
                  {decision.template_language ? ` (${decision.template_language})` : ''}
                </TableCell>
                <TableCell>
                  <Badge variant={statusVariant(decision.status)}>{decision.status}</Badge>
                </TableCell>
                <TableCell className="max-w-[220px] truncate text-xs">{decision.reason || '—'}</TableCell>
                <TableCell className="text-xs">
                  {decision.campaign_execution_id ? (
                    <div>
                      <div>{decision.campaign_execution_status || 'linked'}</div>
                      <div className="truncate text-muted-foreground">{decision.campaign_execution_id.slice(0, 8)}…</div>
                    </div>
                  ) : (
                    '—'
                  )}
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      )}

      <Dialog open={selectedDecision !== null} onOpenChange={(open) => !open && setSelectedDecision(null)}>
        <DialogContent className="max-h-[90vh] max-w-2xl overflow-y-auto">
          <DialogHeader>
            <DialogTitle>Decision details</DialogTitle>
          </DialogHeader>
          {selectedDecision ? (
            <div className="space-y-4 text-sm">
              <div className="grid gap-2 sm:grid-cols-2">
                <div><span className="text-muted-foreground">Status:</span> {selectedDecision.status}</div>
                <div><span className="text-muted-foreground">Created:</span> {formatDate(selectedDecision.created_at)}</div>
                <div><span className="text-muted-foreground">Call ID:</span> {selectedDecision.call_id}</div>
                <div><span className="text-muted-foreground">Phone:</span> {selectedDecision.caller_phone || '—'}</div>
                <div className="sm:col-span-2">
                  <span className="text-muted-foreground">Plantilla elegida:</span>{' '}
                  {selectedDecision.selected_template_name || '—'}
                  {selectedDecision.template_language ? ` (${selectedDecision.template_language})` : ''}
                </div>
                <div className="sm:col-span-2">
                  <span className="text-muted-foreground">ElevenLabs:</span>{' '}
                  {selectedDecision.elevenlabs_conversation_id || '—'}
                </div>
                <div className="sm:col-span-2">
                  <span className="text-muted-foreground">Reason:</span> {selectedDecision.reason || '—'}
                </div>
              </div>

              {selectedDecision.candidate_templates.length > 0 ? (
                <div>
                  <div className="mb-1 font-medium">Candidate templates</div>
                  <div className="text-muted-foreground">{selectedDecision.candidate_templates.join(', ')}</div>
                </div>
              ) : null}

              {selectedDecision.missing_variables.length > 0 ? (
                <div>
                  <div className="mb-1 font-medium">Missing variables</div>
                  <div className="text-destructive">{selectedDecision.missing_variables.join(', ')}</div>
                </div>
              ) : null}

              {selectedDecision.reason ? (
                <div>
                  <div className="mb-1 font-medium">Motivo de la decisión</div>
                  <div className="text-muted-foreground">{resolveDecisionReason(selectedDecision)}</div>
                </div>
              ) : (
                <div>
                  <div className="mb-1 font-medium">Motivo de la decisión</div>
                  <div className="text-muted-foreground">{resolveDecisionReason(selectedDecision)}</div>
                </div>
              )}

              {Object.keys(selectedDecision.resolved_variables || {}).length > 0 ? (
                <div>
                  <div className="mb-1 font-medium">Resolved variables</div>
                  <pre className="overflow-x-auto rounded-md bg-muted/50 p-3 text-xs">
                    {JSON.stringify(selectedDecision.resolved_variables, null, 2)}
                  </pre>
                </div>
              ) : null}

              {selectedDecision.campaign_execution_id ? (
                <div className="space-y-2 rounded-md border border-border p-3">
                  <div className="font-medium">Campaign execution</div>
                  <div><span className="text-muted-foreground">ID:</span> {selectedDecision.campaign_execution_id}</div>
                  <div><span className="text-muted-foreground">Status:</span> {selectedDecision.campaign_execution_status || '—'}</div>
                  <div><span className="text-muted-foreground">Dedupe key:</span> {selectedDecision.campaign_dedupe_key || '—'}</div>
                  {selectedDecision.campaign_attempts.length > 0 ? (
                    <div>
                      <div className="mb-1 font-medium">Attempts</div>
                      <div className="space-y-2">
                        {selectedDecision.campaign_attempts.map((attempt) => (
                          <div key={attempt.id} className="rounded-md bg-muted/40 p-2 text-xs">
                            <div>{attempt.status} · {attempt.channel} · {attempt.provider}</div>
                            {attempt.error_message ? (
                              <div className="text-destructive">{attempt.error_message}</div>
                            ) : null}
                          </div>
                        ))}
                      </div>
                    </div>
                  ) : null}
                </div>
              ) : null}

              {selectedDecision.error_message ? (
                <Alert variant="destructive">
                  <AlertDescription>{selectedDecision.error_message}</AlertDescription>
                </Alert>
              ) : null}
            </div>
          ) : null}
        </DialogContent>
      </Dialog>
    </div>
  )
}
